import {inject} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import {AuthGuardData, createAuthGuard} from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import {ToastService} from '@frontend/toast';
import {APP_ROUTES} from '../../app.routes';

function getRequiredRoles(route: ActivatedRouteSnapshot): string[] {
  return ([] as string[]).concat(route.data['role'] || []).concat(route.data['roles'] || []);
}

function getGrantedRolesList(grantedRoles: AuthGuardData['grantedRoles']): string[] {
  const realmRoles = grantedRoles.realmRoles ?? [];
  const resourceRoles = Object.values(grantedRoles.resourceRoles ?? {}).flat();
  return [...realmRoles, ...resourceRoles];
}

function hasRequiredRole(
  requiredRoles: string[],
  grantedRoles: AuthGuardData['grantedRoles'],
): boolean {
  const userRoles = getGrantedRolesList(grantedRoles);
  return requiredRoles.some((role) => userRoles.includes(role));
}

export const canActivateAuthRole: CanActivateFn = createAuthGuard(
  async (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    authData: AuthGuardData,
  ): Promise<boolean | UrlTree> => {
    const {authenticated, grantedRoles} = authData;
    const router = inject(Router);
    const toast = inject(ToastService);
    const keycloak = inject(Keycloak);

    const required = getRequiredRoles(route);
    if (required.length === 0) {
      return true;
    }

    if (!authenticated) {
      await keycloak.login({redirectUri: window.location.origin + state.url});
      return false;
    }

    if (hasRequiredRole(required, grantedRoles)) {
      return true;
    }

    toast.showError('Access Denied', 'You do not have permission to access this resource');
    const fallback = (route.data['routeDataFallbackRoute'] as string) || APP_ROUTES.START;
    return router.url === fallback ? false : router.createUrlTree([fallback]);
  },
);
